import { supabase } from '../lib/supabase';

export interface Project {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  bodyHtml: string | null;
  coverImageUrl: string | null;
  thumbnailMonochrome?: boolean;
  startDate: string | null;
  endDate: string | null;
  status: 'draft' | 'published' | 'archived';
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  metadata: Record<string, unknown>;
  featured?: boolean;
}

interface ProjectRow {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  body_html?: string | null;
  cover_image_url: string | null;
  metadata: Record<string, unknown>;
  start_date: string | null;
  end_date: string | null;
  status: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  // featured column may or may not exist; ignored if absent
  featured?: boolean;
}

const convertToProject = (row: ProjectRow): Project => ({
  id: row.id,
  title: row.title,
  slug: row.slug,
  description: row.description,
  bodyHtml: row.body_html ?? null,
  coverImageUrl: row.cover_image_url,
  thumbnailMonochrome: (row.metadata?.thumbnailMonochrome as boolean | undefined) ?? false,
  startDate: row.start_date,
  endDate: row.end_date,
  status: row.status as 'draft' | 'published' | 'archived',
  createdBy: row.created_by,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  publishedAt: row.published_at,
  metadata: row.metadata,
  featured: row.featured ?? (row.metadata?.featured as boolean | undefined) ?? false,
});

export const projectService = {
  async getAll(): Promise<{ data: Project[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data?.map(convertToProject) ?? [], 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async getAllPublished(): Promise<{ data: Project[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (error) throw error;

      return { 
        data: data?.map(convertToProject) ?? [], 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async getBySlug(slug: string): Promise<{ data: Project | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (error) throw error;

      return { 
        data: data ? convertToProject(data) : null, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async create(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<{ data: Project | null; error: Error | null }> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const insertData = {
        title: project.title,
        slug: project.slug,
        description: project.description,
        body_html: project.bodyHtml,
        cover_image_url: project.coverImageUrl,
        start_date: project.startDate,
        end_date: project.endDate,
        status: project.status,
        created_by: userData.user.id,
        published_at: project.publishedAt,
        metadata: { ...(project.metadata || {}), featured: project.featured ?? false, thumbnailMonochrome: project.thumbnailMonochrome ?? false },
      };

      const { data, error } = await supabase
        .from('projects')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { 
        data: data ? convertToProject(data) : null, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async update(id: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'updatedAt'>>): Promise<{ data: Project | null; error: Error | null }> {
    try {
      const updateData: Record<string, unknown> = {};
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.slug !== undefined) updateData.slug = updates.slug;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.bodyHtml !== undefined) updateData.body_html = updates.bodyHtml;
      if (updates.coverImageUrl !== undefined) updateData.cover_image_url = updates.coverImageUrl;
      if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
      if (updates.endDate !== undefined) updateData.end_date = updates.endDate;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.publishedAt !== undefined) updateData.published_at = updates.publishedAt;
      if (updates.metadata !== undefined || updates.featured !== undefined || updates.thumbnailMonochrome !== undefined) {
        // Merge existing metadata to avoid overwriting
        const { data: current } = await supabase
          .from('projects')
          .select('metadata')
          .eq('id', id)
          .single();
        const baseMetadata = (current?.metadata as Record<string, unknown>) || {};
        updateData.metadata = {
          ...baseMetadata,
          ...(updates.metadata || {}),
          ...(updates.featured !== undefined ? { featured: updates.featured } : {}),
          ...(updates.thumbnailMonochrome !== undefined ? { thumbnailMonochrome: updates.thumbnailMonochrome } : {}),
        };
      }

      const { data, error } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return { 
        data: data ? convertToProject(data) : null, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: error as Error 
      };
    }
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },
};